import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../../mutation-types';
import CrmDealsAPI from '../../../api/crm/deals';

const PER_PAGE = 15;

function emptyStageEntry() {
    return { deals: [], meta: { total_count: 0, page: 0, per_page: PER_PAGE, total_pages: 0 }, isFetching: false };
}

export const state = {
    records: [],
    meta: { total_count: 0, page: 1, per_page: 25, total_pages: 1 },
    stageRecords: {},
    uiFlags: {
        isFetching: false,
        isLoadingMore: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isMoving: false,
    },
};

export const getters = {
    getUIFlags(_state) {
        return _state.uiFlags;
    },
    getDeals(_state) {
        return _state.records;
    },
    getMeta(_state) {
        return _state.meta;
    },
    getDealsByStage: _state => stageId => {
        return _state.records
            .filter(d => d.crm_pipeline_stage_id === stageId)
            .sort((a, b) => a.position - b.position);
    },
    getDealById: _state => id => {
        return _state.records.find(d => d.id === Number(id));
    },
    hasMoreDeals(_state) {
        return _state.meta.page < _state.meta.total_pages;
    },
    // Per-stage getters
    getStageDeals: _state => stageId => {
        const entry = _state.stageRecords[stageId];
        return entry ? entry.deals : [];
    },
    getStageMeta: _state => stageId => {
        const entry = _state.stageRecords[stageId];
        return entry ? entry.meta : { total_count: 0, page: 0, per_page: PER_PAGE, total_pages: 0 };
    },
    hasMoreForStage: _state => stageId => {
        const entry = _state.stageRecords[stageId];
        if (!entry) return true;
        return entry.meta.page < entry.meta.total_pages;
    },
    isLoadingStage: _state => stageId => {
        const entry = _state.stageRecords[stageId];
        return entry ? entry.isFetching : false;
    },
    getStageTotalCount: _state => stageId => {
        const entry = _state.stageRecords[stageId];
        return entry ? entry.meta.total_count : 0;
    },
    getStageTotalAmount: _state => stageId => {
        const entry = _state.stageRecords[stageId];
        return entry ? (entry.meta.total_amount || 0) : 0;
    },
};

export const actions = {
    // Original global fetch (for list view, etc.)
    get: async ({ commit }, { pipelineId, filters } = {}) => {
        commit(types.SET_CRM_DEALS_UI_FLAG, { isFetching: true });
        try {
            const response = await CrmDealsAPI.getByPipeline(pipelineId, { ...filters, page: 1 });
            commit(types.SET_CRM_DEALS, response.data.data);
            commit('SET_CRM_DEALS_META', response.data.meta);
        } catch (error) {
            // Ignore error
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isFetching: false });
        }
    },
    // Multi-pipeline fetch (for list view with multiple pipelines)
    getMultiPipeline: async ({ commit }, { pipelineIds, filters } = {}) => {
        commit(types.SET_CRM_DEALS_UI_FLAG, { isFetching: true });
        try {
            const response = await CrmDealsAPI.getByPipelineIds(pipelineIds, { ...filters, page: 1 });
            commit(types.SET_CRM_DEALS, response.data.data);
            commit('SET_CRM_DEALS_META', response.data.meta);
        } catch (error) {
            // Ignore error
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isFetching: false });
        }
    },
    loadMore: async ({ commit, state: _state }, { pipelineId, filters } = {}) => {
        if (_state.meta.page >= _state.meta.total_pages) return;
        commit(types.SET_CRM_DEALS_UI_FLAG, { isLoadingMore: true });
        try {
            const nextPage = _state.meta.page + 1;
            const response = await CrmDealsAPI.getByPipeline(pipelineId, { ...filters, page: nextPage });
            commit('APPEND_CRM_DEALS', response.data.data);
            commit('SET_CRM_DEALS_META', response.data.meta);
        } catch (error) {
            // Ignore error
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isLoadingMore: false });
        }
    },

    // Per-stage actions
    getByStage: async ({ commit, state: _state }, { pipelineId, stageId, filters } = {}) => {
        commit('SET_STAGE_FETCHING', { stageId, isFetching: true });
        try {
            const response = await CrmDealsAPI.getByPipeline(pipelineId, {
                ...filters,
                stage_id: stageId,
                page: 1,
                per_page: PER_PAGE,
            });
            commit('SET_STAGE_DEALS', { stageId, deals: response.data.data, meta: response.data.meta });
        } catch (error) {
            // Ignore error
        } finally {
            commit('SET_STAGE_FETCHING', { stageId, isFetching: false });
        }
    },

    loadMoreByStage: async ({ commit, state: _state }, { pipelineId, stageId, filters } = {}) => {
        const entry = _state.stageRecords[stageId];
        if (!entry || entry.meta.page >= entry.meta.total_pages || entry.isFetching) return;
        commit('SET_STAGE_FETCHING', { stageId, isFetching: true });
        try {
            const nextPage = entry.meta.page + 1;
            const response = await CrmDealsAPI.getByPipeline(pipelineId, {
                ...filters,
                stage_id: stageId,
                page: nextPage,
                per_page: PER_PAGE,
            });
            commit('APPEND_STAGE_DEALS', { stageId, deals: response.data.data, meta: response.data.meta });
        } catch (error) {
            // Ignore error
        } finally {
            commit('SET_STAGE_FETCHING', { stageId, isFetching: false });
        }
    },

    getInitialByStages: async ({ commit }, { pipelineId, stageIds, filters } = {}) => {
        commit(types.SET_CRM_DEALS_UI_FLAG, { isFetching: true });
        commit('CLEAR_STAGE_RECORDS');
        try {
            await Promise.all(
                stageIds.map(async stageId => {
                    commit('SET_STAGE_FETCHING', { stageId, isFetching: true });
                    try {
                        const response = await CrmDealsAPI.getByPipeline(pipelineId, {
                            ...filters,
                            stage_id: stageId,
                            page: 1,
                            per_page: PER_PAGE,
                        });
                        commit('SET_STAGE_DEALS', {
                            stageId,
                            deals: response.data.data || [],
                            meta: response.data.meta || {},
                        });
                    } catch {
                        commit('SET_STAGE_FETCHING', { stageId, isFetching: false });
                    }
                })
            );
        } catch (error) {
            // Ignore error
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isFetching: false });
        }
    },


    create: async ({ commit }, dealObj) => {
        commit(types.SET_CRM_DEALS_UI_FLAG, { isCreating: true });
        try {
            const response = await CrmDealsAPI.create(dealObj);
            commit(types.ADD_CRM_DEAL, response.data);
            // Also add to stageRecords
            commit('ADD_DEAL_TO_STAGE', response.data);
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isCreating: false });
        }
    },
    update: async ({ commit }, { id, ...updateObj }) => {
        commit(types.SET_CRM_DEALS_UI_FLAG, { isUpdating: true });
        try {
            const response = await CrmDealsAPI.update(id, updateObj);
            commit(types.EDIT_CRM_DEAL, response.data);
            // Also update in stageRecords
            commit('UPDATE_DEAL_IN_STAGE', response.data);
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isUpdating: false });
        }
    },
    delete: async ({ commit }, id) => {
        commit(types.SET_CRM_DEALS_UI_FLAG, { isDeleting: true });
        try {
            await CrmDealsAPI.delete(id);
            commit(types.DELETE_CRM_DEAL, id);
            // Also remove from stageRecords
            commit('REMOVE_DEAL_FROM_STAGE', id);
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isDeleting: false });
        }
    },
    move: async ({ commit }, { dealId, stageId, position }) => {
        commit(types.SET_CRM_DEALS_UI_FLAG, { isMoving: true });
        try {
            const response = await CrmDealsAPI.move(dealId, stageId, position);
            commit(types.EDIT_CRM_DEAL, response.data);
            // Move deal between stages in stageRecords
            commit('MOVE_DEAL_IN_STAGES', response.data);
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CRM_DEALS_UI_FLAG, { isMoving: false });
        }
    },
};

export const mutations = {
    [types.SET_CRM_DEALS_UI_FLAG](_state, data) {
        _state.uiFlags = { ..._state.uiFlags, ...data };
    },
    [types.SET_CRM_DEALS]: MutationHelpers.set,
    [types.ADD_CRM_DEAL]: MutationHelpers.create,
    [types.EDIT_CRM_DEAL]: MutationHelpers.update,
    [types.DELETE_CRM_DEAL]: MutationHelpers.destroy,
    SET_CRM_DEALS_META(_state, meta) {
        _state.meta = meta;
    },
    APPEND_CRM_DEALS(_state, deals) {
        const existingIds = new Set(_state.records.map(d => d.id));
        const newDeals = deals.filter(d => !existingIds.has(d.id));
        _state.records = [..._state.records, ...newDeals];
    },

    // Per-stage mutations
    CLEAR_STAGE_RECORDS(_state) {
        _state.stageRecords = {};
    },
    SET_STAGE_FETCHING(_state, { stageId, isFetching }) {
        const entry = _state.stageRecords[stageId] || emptyStageEntry();
        _state.stageRecords = {
            ..._state.stageRecords,
            [stageId]: { ...entry, isFetching },
        };
    },
    SET_STAGE_DEALS(_state, { stageId, deals, meta }) {
        let totalCount = meta.total_count;
        let totalAmount = meta.total_amount || 0;
        
        if (meta.counts_by_stage && meta.counts_by_stage[stageId] !== undefined) {
            totalCount = meta.counts_by_stage[stageId];
        }
        if (meta.amounts_by_stage && meta.amounts_by_stage[stageId] !== undefined) {
            totalAmount = parseFloat(meta.amounts_by_stage[stageId]);
        }
        
        _state.stageRecords = {
            ..._state.stageRecords,
            [stageId]: { deals, meta: { ...meta, total_count: totalCount, total_amount: totalAmount }, isFetching: false },
        };
    },
    APPEND_STAGE_DEALS(_state, { stageId, deals, meta }) {
        const entry = _state.stageRecords[stageId] || emptyStageEntry();
        const existingIds = new Set(entry.deals.map(d => d.id));
        const newDeals = deals.filter(d => !existingIds.has(d.id));
        
        let totalCount = meta.total_count;
        let totalAmount = meta.total_amount || 0;
        
        if (meta.counts_by_stage && meta.counts_by_stage[stageId] !== undefined) {
            totalCount = meta.counts_by_stage[stageId];
        }
        if (meta.amounts_by_stage && meta.amounts_by_stage[stageId] !== undefined) {
            totalAmount = parseFloat(meta.amounts_by_stage[stageId]);
        }

        _state.stageRecords = {
            ..._state.stageRecords,
            [stageId]: { deals: [...entry.deals, ...newDeals], meta: { ...meta, total_count: totalCount, total_amount: totalAmount }, isFetching: false },
        };
    },
    ADD_DEAL_TO_STAGE(_state, deal) {
        const stageId = deal.crm_pipeline_stage_id;
        const entry = _state.stageRecords[stageId];
        if (!entry) return;
        const exists = entry.deals.some(d => d.id === deal.id);
        if (!exists) {
            _state.stageRecords = {
                ..._state.stageRecords,
                [stageId]: {
                    ...entry,
                    deals: [deal, ...entry.deals],
                    meta: { ...entry.meta, total_count: entry.meta.total_count + 1 },
                },
            };
        }
    },
    UPDATE_DEAL_IN_STAGE(_state, deal) {
        const stageId = deal.crm_pipeline_stage_id;
        // Update in all stages (deal might have changed stage via update, not move)
        Object.keys(_state.stageRecords).forEach(sid => {
            const entry = _state.stageRecords[sid];
            const idx = entry.deals.findIndex(d => d.id === deal.id);
            if (idx !== -1) {
                if (Number(sid) === stageId) {
                    // Same stage — update in place
                    const updatedDeals = [...entry.deals];
                    updatedDeals[idx] = deal;
                    const newTotal = updatedDeals.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
                    _state.stageRecords = {
                        ..._state.stageRecords,
                        [sid]: { ...entry, deals: updatedDeals, meta: { ...entry.meta, total_amount: newTotal } },
                    };
                } else {
                    // Deal moved to different stage via update — remove from old
                    const filtered = entry.deals.filter(d => d.id !== deal.id);
                    const newTotal = filtered.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
                    _state.stageRecords = {
                        ..._state.stageRecords,
                        [sid]: { ...entry, deals: filtered, meta: { ...entry.meta, total_count: Math.max(0, entry.meta.total_count - 1), total_amount: newTotal } },
                    };
                    // Add to new stage
                    const newEntry = _state.stageRecords[stageId];
                    if (newEntry && !newEntry.deals.some(d => d.id === deal.id)) {
                        const newEntryDeals = [deal, ...newEntry.deals];
                        const newEntryTotal = newEntryDeals.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
                        _state.stageRecords = {
                            ..._state.stageRecords,
                            [stageId]: {
                                ...newEntry,
                                deals: newEntryDeals,
                                meta: { ...newEntry.meta, total_count: newEntry.meta.total_count + 1, total_amount: newEntryTotal },
                            },
                        };
                    }
                }
            }
        });
    },
    REMOVE_DEAL_FROM_STAGE(_state, dealId) {
        Object.keys(_state.stageRecords).forEach(sid => {
            const entry = _state.stageRecords[sid];
            const idx = entry.deals.findIndex(d => d.id === dealId);
            if (idx !== -1) {
                _state.stageRecords = {
                    ..._state.stageRecords,
                    [sid]: {
                        ...entry,
                        deals: entry.deals.filter(d => d.id !== dealId),
                        meta: { ...entry.meta, total_count: Math.max(0, entry.meta.total_count - 1) },
                    },
                };
            }
        });
    },
    MOVE_DEAL_IN_STAGES(_state, deal) {
        const newStageId = deal.crm_pipeline_stage_id;
        // Remove from old stage(s)
        Object.keys(_state.stageRecords).forEach(sid => {
            if (Number(sid) === newStageId) return;
            const entry = _state.stageRecords[sid];
            const idx = entry.deals.findIndex(d => d.id === deal.id);
            if (idx !== -1) {
                const removedDeals = entry.deals.filter(d => d.id !== deal.id);
                const removedTotal = removedDeals.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
                _state.stageRecords = {
                    ..._state.stageRecords,
                    [sid]: {
                        ...entry,
                        deals: removedDeals,
                        meta: { ...entry.meta, total_count: Math.max(0, entry.meta.total_count - 1), total_amount: removedTotal },
                    },
                };
            }
        });
        // Add/update in new stage
        const newEntry = _state.stageRecords[newStageId];
        if (newEntry) {
            const existing = newEntry.deals.findIndex(d => d.id === deal.id);
            let updatedDeals;
            if (existing !== -1) {
                updatedDeals = [...newEntry.deals];
                updatedDeals[existing] = deal;
            } else {
                updatedDeals = [deal, ...newEntry.deals];
            }
            const newEntryTotal = updatedDeals.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
            _state.stageRecords = {
                ..._state.stageRecords,
                [newStageId]: {
                    ...newEntry,
                    deals: updatedDeals,
                    meta: existing === -1
                        ? { ...newEntry.meta, total_count: newEntry.meta.total_count + 1, total_amount: newEntryTotal }
                        : { ...newEntry.meta, total_amount: newEntryTotal },
                },
            };
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
